import { NavigationPathsEnum, PaymentMethodeEnum, PromotionTypeEnum } from "@utilities/enums";
import { useBasket, useClient, useNavigation, usePromotion, useSales } from "@utilities/hooks";
import { MultiplePaymentMethod } from "@utilities/types";
import { useCallback, useMemo, useState } from "react";

export const useController = () => {

    const { goBack, navigateTo } = useNavigation();
    const { selectedSale, setSelectedSale, onCreateSale } = useSales();
    const {
        activeBasket, totalBasket,
        totalToPay, onDeleteBasket,
        setActiveBasket,
    } = useBasket();
    const {
        clients, selectedClient, createClientSuccess,
        setSelectedClient, setCreateClientSuccess,
        onFindClient, onCreateClient
    } = useClient();
    const {
        selectedPromotion, getOnePromotionError,
        setSelectedPromotion, setGetOnePromotionError,
        onGetOnePromotion,
    } = usePromotion();

    const [stepIndex, setStepIndex] = useState<number>(0);
    const [paymentMethods, setPaymentMethods] = useState<MultiplePaymentMethod[]>([]);

    // Calcul de la remise si promotion appliquée
    const discountAmount = useMemo(() => {
        if (!selectedPromotion || !selectedPromotion.isActive) return 0;

        if (selectedPromotion.type === PromotionTypeEnum.PERCENTAGE) {
            return totalBasket * (selectedPromotion.value / 100);
        }
        return selectedPromotion.value;
    }, [selectedPromotion, totalBasket]);

    // Total après remise
    const discountedTotal = useMemo(() => {
        return Math.max(0, totalBasket - discountAmount);
    }, [totalBasket, discountAmount]);

    // Calcul du total payé par le client
    const clientTotalPay = useMemo(() =>
        paymentMethods.reduce((sum, p) => sum + p.amount, 0),
        [paymentMethods]);

    // Montant restant à payer (basé sur le total avec remise)
    const remainingAmount = useMemo(() =>
        Math.max(0, discountedTotal - clientTotalPay),
        [discountedTotal, clientTotalPay]);

    // Vérifie si le paiement est complet (basé sur le total avec remise)
    const isPaymentComplete = useMemo(() =>
        clientTotalPay >= discountedTotal && paymentMethods.length > 0,
        [clientTotalPay, discountedTotal, paymentMethods]);

    // Vérifie si c'est un paiement à crédit (au moins une méthode crédit)
    const hasCreditPayment = useMemo(() =>
        paymentMethods.some(p => p.methode === PaymentMethodeEnum.credit),
        [paymentMethods]);

    const handleGoBack = useCallback(() => {
        if (stepIndex === 0) {
            goBack();
        } else {
            setStepIndex((prev) => prev - 1);
        }
    }, [stepIndex, goBack]);

    // Ajouter un paiement
    const onAddPayment = useCallback((payment: MultiplePaymentMethod) => {
        setPaymentMethods(prev => [...prev, payment]);
    }, []);

    // Supprimer un paiement
    const onRemovePayment = useCallback((index: number) => {
        setPaymentMethods(prev => prev.filter((_, i) => i !== index));
    }, []);

    // Mettre à jour un paiement
    const onUpdatePayment = useCallback((index: number, payment: MultiplePaymentMethod) => {
        setPaymentMethods(prev => prev.map((p, i) => i === index ? payment : p));
    }, []);

    // Réinitialiser les paiements
    const resetPayments = useCallback(() => {
        setPaymentMethods([]);
    }, []);

    const onClearTransaction = useCallback(async () => {
        if (activeBasket) {
            await onDeleteBasket(activeBasket._id);
        }
        setActiveBasket(null);
        setSelectedClient(null);
        setSelectedPromotion(null);
        resetPayments();
    }, [
        activeBasket, onDeleteBasket,
        setActiveBasket, setSelectedClient,
        setSelectedPromotion, resetPayments,
    ]);

    const handleGoToNextStep = useCallback(async (index: number) => {
        if (index === 2) {
            // Vérifier qu'au moins un paiement est ajouté
            if (paymentMethods.length === 0) return;

            // Pour les paiements avec crédit, le client peut payer moins que le total
            // Pour les autres, le paiement doit être complet
            if (!hasCreditPayment && clientTotalPay < discountedTotal) return;

            await onCreateSale({
                ...(selectedClient && { clientID: selectedClient?._id }),
                ...(selectedPromotion && { promotion: selectedPromotion }),
                basket: activeBasket?.basket,
                totalPay: clientTotalPay,
                paymentMethods: paymentMethods,
            });
            onClearTransaction();
        }
        setStepIndex(index);
    }, [
        paymentMethods, selectedClient, hasCreditPayment,
        activeBasket, totalToPay, selectedPromotion,
        clientTotalPay, onCreateSale, onClearTransaction,
    ]);

    const onShopRedirect = useCallback(async () => {
        setSelectedSale(null);
        navigateTo(NavigationPathsEnum.SHOP);
    }, [navigateTo, setSelectedSale]);

    const handleApplyPromotion = useCallback(async (code: string) => {
        await onGetOnePromotion(code);
    }, [onGetOnePromotion]);

    const handleRemovePromotion = useCallback(() => {
        setSelectedPromotion(null);
        setGetOnePromotionError(null);
    }, [setSelectedPromotion, setGetOnePromotionError]);

    return {
        paymentMethods, totalBasket, totalToPay,
        activeBasket, clients, stepIndex,
        selectedClient, createClientSuccess,
        clientTotalPay, selectedSale, getOnePromotionError,
        selectedPromotion, handleApplyPromotion, handleRemovePromotion,
        isPaymentComplete, remainingAmount, hasCreditPayment,
        discountAmount, discountedTotal,
        onShopRedirect, onAddPayment, onRemovePayment, onUpdatePayment,
        setSelectedClient, setCreateClientSuccess,
        handleGoBack, onFindClient, onCreateClient, handleGoToNextStep,
    };
};
